import type { Zygosity } from '../types/methylation';

/**
 * Map a normalized genotype to zygosity relative to the rule's risk alleles.
 *
 * For X-linked variants in males, a single-letter genotype is treated as
 * hemizygous: presence of a risk allele yields `+/-` (heterozygous-equivalent),
 * absence yields `-/-`.
 */
export const calculateZygosity = ({
  genotype,
  riskAlleles,
  isXLinked = false,
  isMale = false,
}: {
  genotype: string;
  riskAlleles: string[];
  isXLinked?: boolean;
  isMale?: boolean;
}): Zygosity => {
  if (!genotype || genotype === '--' || genotype === '00') {
    return 'missing';
  }

  if (isXLinked && isMale && genotype.length === 1) {
    const hasRiskAllele = riskAlleles.includes(genotype.toUpperCase());
    return hasRiskAllele ? '+/-' : '-/-';
  }

  let riskAlleleCount = 0;
  for (const allele of genotype.toUpperCase()) {
    if (riskAlleles.includes(allele)) {
      riskAlleleCount++;
    }
  }

  switch (riskAlleleCount) {
    case 0:
      return '-/-';
    case 1:
      return '+/-';
    case 2:
      return '+/+';
    default:
      return 'missing';
  }
};
